import { FormlyFieldConfig } from '@ngx-formly/core';
import type { FormlyHookFn } from './formly-hook-fn.type';
import { getChildObject, getFormRoot } from './form-data.utility';
import {
  debounceTime,
  filter,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import {
  FormlyDropdownValue,
  InverterSearchModel,
} from '@intellio/shared/models';
import { InvertersService } from '@intellio/shared/services';

export const filterInverterModelsHook = (
  invertersService: InvertersService
): FormlyHookFn => {
  return (field: FormlyFieldConfig) => {
    const p = field.parent;
    const mfgObj = getChildObject(p, 'inverterManufacturer');
    const parentFormField = p.parent.formControl;
    const mfgFormField = parentFormField.get(mfgObj.key);
    const modelObj = getChildObject(p, 'inverterModel');

    const root = getFormRoot(field);
    const mfg$ = mfgFormField.valueChanges.pipe(
      startWith(`${mfgFormField.value}:initial`),
      tap((value) => {
        if (field.form.status === 'DISABLED') {
          field.form.get(modelObj.key).disable();
        }
        if (field.form.status !== 'DISABLED') {
          const model = parentFormField.get(modelObj.key);
          const isStartValue =
            value === null ? false : value?.toString().split(':').length > 1;
          if (!isStartValue) {
            model.setValue('');
          }

          const ratingkWObj = getChildObject(p, 'inverterRatingkW');
          const ratingkW = parentFormField.get(ratingkWObj.key);
          if (!isStartValue) {
            ratingkW.setValue('');
          }

          const ratingkVAObj = getChildObject(p, 'inverterRatingKVA');
          const ratingkVA = parentFormField.get(ratingkVAObj.key);
          if (!isStartValue) {
            ratingkVA.setValue('');
          }

          const ratedVoltageObj = getChildObject(p, 'inverterRatingVoltage');
          const ratedVolts = parentFormField.get(ratedVoltageObj.key);
          if (!isStartValue) {
            ratedVolts.setValue('');
          }

          const ratedDCVoltageObj = getChildObject(p, 'inverterDCRatedVoltage');
          const ratedDCVolts = parentFormField.get(ratedDCVoltageObj?.key);
          if (!isStartValue) {
            ratedDCVolts?.setValue('');
          }

          const noOfInverterObj = getChildObject(p, 'numberOfInverter');
          const noOfInverter = parentFormField.get(noOfInverterObj?.key);
          if (!isStartValue) {
            noOfInverter?.setValue(0);
          }

          const totalInverterRatingObj = getChildObject(
            p,
            'totalInverterRating'
          );
          const totalInverterRating = parentFormField.get(
            totalInverterRatingObj?.key
          );
          if (!isStartValue) {
            totalInverterRating?.setValue(0);
            if (totalInverterRatingObj && totalInverterRatingObj.formControl) {
              totalInverterRatingObj.formControl.defaultValue = 0;
            }
          }

          const ratedDCCircuitVoltsObj = getChildObject(
            p,
            'inverterDCCircuitVoltage'
          );
          const ratedDCCircuitVolts = field.form.get(
            ratedDCCircuitVoltsObj?.key
          );
          if (!isStartValue) {
            ratedDCCircuitVolts?.setValue('');
          }

          const inverterIsCertifiedObj = getChildObject(
            p,
            'inverterIsCertified'
          );
          const inverterIsCertified = field.form.get(
            inverterIsCertifiedObj?.key
          );
          if (!isStartValue) {
            inverterIsCertified?.setValue('');
          }
        }
      })
    );

    const search$ =
      field?.options?.fieldChanges?.pipe(
        filter(
          (e: any) =>
            e?.type === 'typeaheadInputChanges' && e?.field?.id === field.id
        ),
        debounceTime(200),
        map((e: any) => e?.value ?? ''),
        startWith('')
      ) ?? of('');

    field.props.options = combineLatest([mfg$, search$]).pipe(
      switchMap(([mfg, search]) => {
        if (mfg?.toString().includes(':initial')) {
          mfg = mfg.replace(':initial', '');
        }

        if (!mfg || mfg === 'undefined' || mfg === 'null') {
          return of([]);
        }

        const modelField = getChildObject(
          root,
          'showCertifiedInverter'
        ) as FormlyFieldConfig;
        var showCertifiedInverters;

        if (modelField) {
          showCertifiedInverters = modelField['showCertifiedInverter'];
        } else {
          showCertifiedInverters = 0;
        }

        const results = invertersService
          .searchInverters(
            new InverterSearchModel({
              Manufacturer: mfg,
              Search: search.length > 0 ? search : undefined,
              Take: 40,
            })
          )
          .pipe(
            map((response) => {
              const filteredInverters = (response?.data?.data ?? []).filter(
                (inv) =>
                  showCertifiedInverters === 1
                    ? inv.uL1741CertificationDate !== null
                    : true
              );
              let options: FormlyDropdownValue[] = filteredInverters.map(
                (inv) => ({
                  value: inv.modelNumber,
                  label: inv.modelNumber,
                  disabled: !inv.isActive,
                })
              );
              if (field.props?.allowOtherOption) {
                options = options.concat({
                  value: 'Other',
                  label: 'Other',
                });
              }
              return options;
            })
          );
        return results;
      })
    );
  };
};
